import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Crown, MapPin, Trophy, MessageSquare, Camera, Calendar, ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProfile() {
  const { username } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const T = lang === "fr" ? FR : EN;

  useEffect(() => {
    axios.get(`${API}/users/by-username/${encodeURIComponent(username)}`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }, [username]);

  if (error) return (
    <div className="min-h-screen bg-white">
      <NavBar />
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="profile-not-found">
        <h1 className="font-serif-mystery text-3xl mb-4">{T.notFound}</h1>
        <Link to="/"><Button className="bg-[#8b6f3d] text-white rounded-full h-11 px-6">{T.backHome}</Button></Link>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-white">
      <NavBar />
      <div className="text-center py-20 text-zinc-500">{T.loading}</div>
    </div>
  );

  const { user, stats, comments, medias } = data;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="public-profile-page">
      <NavBar />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/" className="flex items-center gap-2 text-zinc-500 hover:text-[#8b6f3d] mb-6 text-sm">
          <ArrowLeft size={14} /> {T.back}
        </Link>

        {/* Hero */}
        <div className={`rounded-3xl p-8 mb-8 border ${user.is_admin ? "border-[#8b6f3d] bg-gradient-to-br from-amber-100/60 to-white" : "border-zinc-200 bg-zinc-50"}`}>
          <div className="flex items-start gap-6 flex-wrap">
            {user.picture ? (
              <img src={user.picture} alt={user.username} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-xl" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-zinc-300 flex items-center justify-center text-3xl text-white shadow-xl">
                {user.username?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-serif-mystery text-4xl mb-1 flex items-center gap-2 flex-wrap" data-testid="profile-username">
                {user.username}
                {user.is_admin && <Crown size={24} className="text-[#8b6f3d]" />}
              </h1>
              {user.is_admin && (
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#8b6f3d] mb-2">
                  {T.creator}
                </div>
              )}
              {user.country && (
                <div className="flex items-center gap-1 text-sm text-zinc-600 mb-3">
                  <MapPin size={13} /> {user.country}
                </div>
              )}
              {user.custom_badges?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {user.custom_badges.map((b, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-[#8b6f3d]/50 text-xs font-medium">
                      <span className="text-base">{b.icon}</span> {b[lang]}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          <StatCard icon={<Trophy size={20} />} value={stats.badges_count} label={T.badges} testId="stat-badges" />
          <StatCard icon={<MapPin size={20} />} value={stats.total_visits} label={T.visits} testId="stat-visits" />
          <StatCard icon={<Crown size={20} />} value={stats.rank ? `#${stats.rank}` : "—"} label={T.rank} testId="stat-rank" />
        </div>

        {/* Publications */}
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-4 flex items-center gap-2">
              <MessageSquare size={18} /> {T.comments} ({comments.length})
            </h2>
            {comments.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">{T.noComments}</p>
            ) : comments.slice(0, 10).map(c => (
              <div key={c.id} className="border border-zinc-200 rounded-xl p-4 mb-3">
                <p className="text-sm text-zinc-700 line-clamp-3">{c.text}</p>
                <div className="flex items-center gap-1 text-xs text-zinc-400 mt-2">
                  <Calendar size={11} /> {new Date(c.created_at).toLocaleDateString(lang)}
                </div>
              </div>
            ))}
          </div>
          <div>
            <h2 className="font-serif-mystery text-2xl text-[#8b6f3d] mb-4 flex items-center gap-2">
              <Camera size={18} /> {T.media} ({medias.length})
            </h2>
            {medias.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">{T.noMedia}</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {medias.map(m => (
                  <a key={m.id} href={`${API}${m.url}`} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-zinc-200">
                    <img src={`${API}${m.url}`} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function StatCard({ icon, value, label, testId }) {
  return (
    <div className="border border-zinc-200 rounded-2xl p-5 bg-white text-center" data-testid={testId}>
      <div className="w-10 h-10 mx-auto rounded-full bg-[#8b6f3d]/10 text-[#8b6f3d] flex items-center justify-center mb-2">{icon}</div>
      <div className="font-serif-mystery text-3xl text-zinc-900">{value}</div>
      <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}

const FR = {
  loading: "Chargement du profil…",
  notFound: "Profil introuvable",
  backHome: "Retour à l'accueil",
  back: "Retour",
  creator: "Créateur de WorldMysteriesMap",
  badges: "Badges",
  visits: "Lieux visités",
  rank: "Classement",
  comments: "Commentaires",
  media: "Médias",
  noComments: "Aucun commentaire pour l'instant.",
  noMedia: "Aucun média pour l'instant.",
};
const EN = {
  loading: "Loading profile…",
  notFound: "Profile not found",
  backHome: "Back home",
  back: "Back",
  creator: "WorldMysteriesMap Creator",
  badges: "Badges",
  visits: "Places visited",
  rank: "Rank",
  comments: "Comments",
  media: "Media",
  noComments: "No comments yet.",
  noMedia: "No media yet.",
};
